/**
 * Sub-tab "Comparação Submercados" do Controle PLD (Task #190 Fase 2).
 *
 * Gráfico de 4 séries (SE/S/NE/N) do PLD diário do mês + tabela cross por
 * semana operativa (Semana | SE | S | NE | N | Δ NE−SE). Como o backend é
 * por submercado, combinamos 4 chamadas client-side (usePld*All).
 *
 * #223 (Veiga): a coluna de spread passa de "Δ máx vs SE" (maior |sub−SE|) para
 * "Δ NE−SE" SINALIZADO — a Indra tem POSIÇÃO em NE, então o spread NE−SE é o que
 * importa pro negócio (não o maior desvio genérico).
 *
 * Simplificação de produto (Cowork+Veiga): substitui o "Dessem vs Decomp" do
 * protótipo — só temos PLD diário hoje, não Decomp.
 */
import { useMemo } from "react";
import type { PldSubmercado } from "../../../api/types";
import { usePldOverviewAll } from "../../../hooks/usePldOverview";
import { usePldSemanasAll } from "../../../hooks/usePldSemanas";
import { PldLineChart, type PldChartPoint } from "./PldLineChart";
import { PldEmpty, PldError, PldLoading } from "./PldStates";
import {
  SUBMERCADO_COLOR,
  SUBMERCADO_LABEL,
  SUBMERCADOS,
  classBySign,
  fmtDif,
  fmtPldNum,
} from "./pldShared";

interface Props {
  mesRef: string;
}

export function PldComparacaoTab({ mesRef }: Props) {
  const overviews = usePldOverviewAll(mesRef);
  const semanas = usePldSemanasAll(mesRef);

  // Pontos diários: 1 linha por dia com as 4 séries (union das datas).
  const pontos = useMemo<PldChartPoint[]>(() => {
    const byDate = new Map<string, PldChartPoint>();
    for (const sub of SUBMERCADOS) {
      const dados = overviews.bySubmercado[sub]?.dados_diarios ?? [];
      for (const d of dados) {
        const dia = d.data.slice(8, 10);
        const row = byDate.get(d.data) ?? { dia };
        row[sub] = d.pld_media_dia;
        byDate.set(d.data, row);
      }
    }
    return [...byDate.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([, row]) => row);
  }, [overviews.bySubmercado]);

  // Cross por semana: usa as semanas do SE como espinha (revisões/períodos).
  const linhasCross = useMemo(() => {
    const espinha = semanas.bySubmercado.SE?.semanas ?? [];
    return espinha.map((s) => {
      const valores: Record<PldSubmercado, number | null> = {
        SE: null, S: null, NE: null, N: null,
      };
      for (const sub of SUBMERCADOS) {
        const match = semanas.bySubmercado[sub]?.semanas.find((x) => x.revisao === s.revisao);
        valores[sub] = match?.pld_medio ?? null;
      }
      // #223: Δ NE−SE SINALIZADO (spread da posição em NE). null se faltar NE ou SE.
      const deltaNE =
        valores.NE !== null && valores.SE !== null ? valores.NE - valores.SE : null;
      return { revisao: s.revisao, valores, deltaNE };
    });
  }, [semanas.bySubmercado]);

  if (overviews.error) return <PldError />;
  if (overviews.isLoading) return <PldLoading />;

  const temDados = pontos.length > 0;
  if (!temDados) return <PldEmpty />;

  return (
    <div data-testid="pld-comparacao">
      <p className="text-white/50 text-xs mb-2">
        PLD diário das 4 submercados no mês — R$/MWh.
      </p>
      <div className="mb-6">
        <PldLineChart
          points={pontos}
          xKey="dia"
          series={SUBMERCADOS.map((sub) => ({
            key: sub,
            label: SUBMERCADO_LABEL[sub],
            color: SUBMERCADO_COLOR[sub],
          }))}
          testId="pld-comparacao-chart"
        />
      </div>

      <h3 className="text-white font-semibold text-sm mb-2">
        PLD médio por semana operativa
      </h3>
      <div className="overflow-x-auto rounded-xl border border-white/10">
        <table className="w-full text-sm">
          <thead className="bg-indra-azul-surface text-white/60 text-[11px] uppercase tracking-wider">
            <tr>
              <th className="px-3 py-2 text-left">Semana</th>
              {SUBMERCADOS.map((sub) => (
                <th key={sub} className="px-3 py-2 text-right">{sub}</th>
              ))}
              <th
                className="px-3 py-2 text-right"
                title="Spread NE − SE (sinalizado): positivo = NE acima de SE; negativo = NE abaixo. Indra tem posição em NE."
              >
                Δ NE−SE
              </th>
            </tr>
          </thead>
          <tbody>
            {linhasCross.map((l) => (
              <tr
                key={l.revisao}
                className="border-t border-white/5 hover:bg-white/5"
                data-testid="pld-cross-row"
              >
                <td className="px-3 py-2 text-white/90 font-semibold">{l.revisao}</td>
                {SUBMERCADOS.map((sub) => (
                  <td key={sub} className="px-3 py-2 text-right font-mono text-white/80">
                    {fmtPldNum(l.valores[sub])}
                  </td>
                ))}
                <td
                  className={`px-3 py-2 text-right font-mono ${
                    l.deltaNE === null ? "text-white/40" : classBySign(l.deltaNE)
                  }`}
                  data-testid="pld-cross-delta-ne"
                >
                  {fmtDif(l.deltaNE)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
